/**
 * Return to where the child was after iOS kills the WebView's content process
 * (ipad-native-shell task 16.4, the JS half).
 *
 * When memory runs short — a heavy world plus a few big apps in the background
 * — iOS terminates the WKWebView content process. Capacitor's
 * `webViewWebContentProcessDidTerminate` answers with `webView.reload()`
 * (`@capacitor/ios` WebViewDelegationHandler.swift). The URL survives that,
 * but this app's location does not: an open project or building is React
 * state in AppShell, and a `?return_to=` deep link is stripped from the URL
 * once consumed. So the child would land back in their town, mid-build, for no
 * reason they can see.
 *
 * AppShell records where it is (`recordResumePoint`), in the same grammar as
 * `?return_to=` (`project:<id>`, `building:<ref>` — see components/returnTo.ts),
 * and App.tsx feeds `consumeResumePoint` into that existing deep-link path. It
 * restores ONLY when all hold:
 *   - native shell (the web keeps today's refresh behaviour);
 *   - this document load is a reload (Navigation Timing `type === "reload"`),
 *     which is what the crash recovery does — a cold app launch is a
 *     "navigate" and should open at home;
 *   - the same account is signed in (siblings share iPads);
 *   - the record is recent (RESUME_MAX_AGE_MS).
 * localStorage, not sessionStorage: it is on disk, so it survives the content
 * process dying no matter which WebKit process holds session storage.
 */

import { isNativeShell } from "../api";

const KEY = "wbm.resumePoint";

/** A crash-and-reload happens within seconds; anything older is a stale record
 *  from an earlier session that was never cleared (e.g. the app was killed). */
export const RESUME_MAX_AGE_MS = 10 * 60 * 1000;

interface ResumeRecord {
  target: string;
  userId: string;
  at: number;
}

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Where AppShell is now, or `null` for the town / home. Native only. */
export function recordResumePoint(userId: string, target: string | null): void {
  if (!isNativeShell()) return;
  const s = storage();
  if (!s) return;
  try {
    if (!target || !userId) {
      s.removeItem(KEY);
      return;
    }
    const rec: ResumeRecord = { target, userId, at: Date.now() };
    s.setItem(KEY, JSON.stringify(rec));
  } catch {
    /* storage full or blocked — resuming is a nicety */
  }
}

function isReload(): boolean {
  try {
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    return nav?.type === "reload";
  } catch {
    return false;
  }
}

let consumed: { record: ResumeRecord | null } | null = null;

/**
 * The `return_to` value to restore for `userId`, or `undefined`. The stored
 * record is read and cleared on the first call of the page load (so a later
 * cold launch never replays it), and the answer is memoised — App.tsx calls
 * this during render.
 */
export function consumeResumePoint(userId: string): string | undefined {
  if (!consumed) {
    consumed = { record: null };
    if (isNativeShell()) {
      const s = storage();
      try {
        const raw = s?.getItem(KEY);
        s?.removeItem(KEY);
        if (raw && isReload()) {
          const rec = JSON.parse(raw) as Partial<ResumeRecord>;
          if (
            typeof rec.target === "string" &&
            typeof rec.userId === "string" &&
            typeof rec.at === "number" &&
            Date.now() - rec.at <= RESUME_MAX_AGE_MS
          ) {
            consumed.record = rec as ResumeRecord;
          }
        }
      } catch {
        /* unreadable record — start at home */
      }
    }
  }
  const rec = consumed.record;
  return rec && rec.userId === userId ? rec.target : undefined;
}

/** TEST-ONLY. */
export function __resetResumeForTest(): void {
  consumed = null;
}
